/**
 * Consentimiento de cookies (P-01, `docs/legal/politica-de-cookies.md` § 4-5): «Seguir navegando
 * no es consentir». La elección se recuerda en el navegador — `localStorage`, no una tabla del
 * backend: no hay un documento `COOKIES` en `LEGAL_DOCUMENTS`
 * (`src/common/src/contracts/legalAcceptances/index.ts`), así que esto nunca llama a
 * `POST /api/legal-acceptances` (eso es para los documentos que sí exige aceptar con sesión —
 * ver `features/legal/api/useAceptarLegal.ts`).
 *
 * Tres estados, no dos: `null` (todavía no ha decidido — el aviso se muestra), `'aceptado'` y
 * `'rechazado'`. Ausencia de decisión nunca se trata como aceptación ni como rechazo.
 */
export type ConsentimientoCookies = 'aceptado' | 'rechazado';

const CLAVE_ALMACENAMIENTO = 'diario-legal-cookies';

/** Envuelto en `try`/`catch`: `localStorage` puede lanzar (navegación privada de Safari, cuota
 * agotada, entorno sin `window`) y un fallo de lectura/escritura no debe romper la pantalla —
 * el comportamiento seguro por defecto es "todavía no ha decidido", que vuelve a mostrar el
 * aviso. */
export const leerConsentimientoCookies = (): ConsentimientoCookies | null => {
	try {
		const valor = window.localStorage.getItem(CLAVE_ALMACENAMIENTO);
		return valor === 'aceptado' || valor === 'rechazado' ? valor : null;
	} catch {
		return null;
	}
};

export const guardarConsentimientoCookies = (valor: ConsentimientoCookies): void => {
	try {
		window.localStorage.setItem(CLAVE_ALMACENAMIENTO, valor);
	} catch {
		// Sin almacenamiento disponible: el aviso reaparecerá en la próxima visita, que es el
		// mismo comportamiento que "todavía no ha decidido" — no hay nada que romper aquí.
	}
};

/**
 * El punto de enganche para quien añada analítica (Hotjar, `docs/legal/politica-de-cookies.md`
 * § 3) mañana: antes de cargar cualquier script de medición, comprobar
 * `hayConsentimientoAnalitico()`. Hoy no hay analítica en la aplicación — nada la llama todavía —
 * pero el mecanismo ya existe para que ese día no haga falta tocar `avisoCookies.tsx`.
 */
export const hayConsentimientoAnalitico = (): boolean => leerConsentimientoCookies() === 'aceptado';
